import SAFARI from 'url:../assets/human-safari-kherson.jpg'
import OLESHKY_DRONE_SIEGE from 'url:../assets/posters/oct-2026/Stop Russian Drone Siege - Zarina Zabrisky.jpeg'
import { ralliesPhotos } from '../assets/rallies'
import { oct2026Posters } from '../assets/posters/oct-2026'
import { oct2026Photos } from '../assets/rallies/oct-2026'
import {
  CITIES,
  eleventh,
  fourteenth,
  images,
  sixteenth,
  thirteenth,
  twelfth,
  twentieth,
} from './cities'
import {
  OLESHKY_DOCUMENTARY,
  oleshkyResources,
  type ResourceGroup,
} from './oleshkyResources'
import { routes } from '../routes'
import type { VideoRef } from '../components/VideoPlayer/EmbeddedVideo'

export type RallyCity = { img?: string; country: string; name: string; link: string }

export type RallyDay = {
  // translation key under `home.*`, e.g. 'schedule13' -> "13th"
  dayKey: string
  monthKey: string
  monthShortKey: string
  cities: string[]
  // shown in the same schedule cell, directly below the previous day (for days with only a city or two)
  stackWithPrevious?: boolean
}

export type Rally = {
  id: string
  path: string
  // translation namespace for the rally's own page text (heroStop, heroSave, worldwideRally, heroDate)
  i18n: string
  // translation namespace for city entries: `${citiesI18n}.<city>.{city,time,location,...}`
  citiesI18n: string
  hashtags: string
  // translation keys rendered as lines of the description next to the image
  infoKeys: string[]
  image: string
  // translation key for the image alt text
  imageAltKey?: string
  // video shown instead of the image
  video?: VideoRef
  cities: Record<string, RallyCity>
  days: RallyDay[]
  photos?: { src: string; city: string; link?: string }[]
  posters?: { src: string; city: string; label?: string; author?: string; link?: string }[]
  // what the rally calls for; falls back to the UN Commission recommendations when not set
  demands?: { titleKey: string; itemKeys: string[] }
  // key resources; falls back to the Human Safari resources when not set
  resources?: ResourceGroup[]
}

const dec = { monthKey: 'home.december', monthShortKey: 'home.dec' }

// Worldwide rally, December 2025. Still rendered by views/Action.tsx.
export const december2025: Rally = {
  id: 'december2025',
  path: routes.action.path,
  i18n: 'home',
  citiesI18n: 'cities',
  hashtags: '#HumansAreNotTargets #StopHumanSafari #SaveKherson',
  infoKeys: ['home.infoText'],
  image: SAFARI,
  cities: CITIES,
  days: [
    { dayKey: 'schedule11', ...dec, cities: eleventh },
    { dayKey: 'schedule12', ...dec, cities: twelfth },
    { dayKey: 'schedule13', ...dec, cities: thirteenth },
    { dayKey: 'schedule14', ...dec, cities: fourteenth },
    { dayKey: 'schedule16', ...dec, cities: sixteenth },
    { dayKey: 'schedule20', ...dec, cities: twentieth },
  ],
  photos: ralliesPhotos,
  posters: images,
}

// Stop Drone Siege / Save Oleshky, October 2026.
export const oleshky2026: Rally = {
  id: 'oleshky2026',
  path: routes.oleshky.path,
  i18n: 'rallies.oleshky2026',
  citiesI18n: 'rallies.oleshky2026.cities',
  hashtags: '#StopDroneSiege #SaveOleshky #SafePassageNow',
  infoKeys: ['rallies.oleshky2026.infoText1', 'rallies.oleshky2026.infoText2'],
  image: OLESHKY_DRONE_SIEGE,
  imageAltKey: 'rallies.oleshky2026.imageAlt',
  video: OLESHKY_DOCUMENTARY,
  cities: {
    edinburgh: {
      country: 'unitedKingdom',
      name: 'Edinburgh',
      link: 'https://www.instagram.com/p/DdpegPvM4yD/',
    },
    birmingham: { country: 'unitedKingdom', name: 'Birmingham', link: '' },
    gdansk: {
      country: 'poland',
      name: 'Gdańsk',
      link: 'https://www.instagram.com/zjednoczeni_gdansk/',
    },
    nantes: {
      country: 'france',
      name: 'Nantes',
      link: 'https://www.facebook.com/events/1140799082236549',
    },
    nantesOct31: {
      country: 'france',
      name: 'Nantes',
      link: 'https://www.facebook.com/events/1140799082236549',
    },
    ottawa: {
      country: 'canada',
      name: 'Ottawa',
      link: 'https://www.facebook.com/100088553050056/posts/1100188596276232/',
    },
    budapest: {
      country: 'hungary',
      name: 'Budapest',
      link: 'https://www.facebook.com/events/786615894546731/',
    },
    oslo: { country: 'norway', name: 'Oslo', link: '' },
    leipzig: { country: 'germany', name: 'Leipzig', link: '' },
    kiel: {
      country: 'germany',
      name: 'Kiel',
      link: 'https://www.instagram.com/p/Dd8xS2eN8TJ/',
    },
    hamburg: {
      country: 'germany',
      name: 'Hamburg',
      link: 'https://www.facebook.com/permalink.php?story_fbid=pfbid0uWJ7WsJhvSmpVyonA3BPnzt7c4BSUsZJsx6cydi2e9AudZdrfQJCc4XKgCzvZEPcl&id=100085247455655',
    },
    plovdiv: {
      country: 'bulgaria',
      name: 'Plovdiv',
      link: 'https://www.facebook.com/events/1129021009797960/',
    },
    malaga: {
      country: 'spain',
      name: 'Málaga',
      link: 'https://www.instagram.com/p/Dd3f_mANd8F/',
    },
    dresden: {
      country: 'germany',
      name: 'Dresden',
      link: 'https://www.instagram.com/p/Dd3TSa3itOw/',
    },
    chicago: {
      country: 'usa',
      name: 'Chicago',
      link: 'https://www.instagram.com/p/DdrsY54hYnK/',
    },
    chicagoOct4: {
      country: 'usa',
      name: 'Chicago',
      link: 'https://www.instagram.com/p/Dd22g_4RXi5/',
    },
    chicagoOct11: {
      country: 'usa',
      name: 'Chicago',
      link: 'https://www.instagram.com/p/DdrsY54hYnK/',
    },
    chicagoOct18: {
      country: 'usa',
      name: 'Chicago',
      link: 'https://www.instagram.com/p/DdrsY54hYnK/',
    },
    brussels: {
      country: 'belgium',
      name: 'Brussels',
      link: 'https://www.instagram.com/p/Ddon6ADDdSq/',
    },
    dublin: {
      country: 'ireland',
      name: 'Dublin',
      link: 'https://www.instagram.com/p/Ddw3XcVuHit/',
    },
    dublinOct11: {
      country: 'ireland',
      name: 'Dublin',
      link: 'https://www.instagram.com/p/DePMFG1uVvE/',
    },
    dublinOct25: {
      country: 'ireland',
      name: 'Dublin',
      link: 'https://www.instagram.com/p/DekQ82El3F4/',
    },
    lyon: {
      country: 'france',
      name: 'Lyon',
      link: 'https://www.instagram.com/p/DdtxKa9F9Dv/',
    },
    paris: {
      country: 'france',
      name: 'Paris',
      link: 'https://www.facebook.com/uniondesukrainiensdefrance',
    },
    parisSep30: {
      country: 'france',
      name: 'Paris',
      link: 'https://www.facebook.com/uniondesukrainiensdefrance',
    },
    sydney: {
      country: 'australia',
      name: 'Sydney',
      link: 'https://www.facebook.com/groups/Ukrainians.sydney/permalink/28344744821815139/',
    },
    manchester: {
      country: 'unitedKingdom',
      name: 'Manchester',
      link: 'https://www.instagram.com/p/DdoCIrsu0Ot/',
    },
    vienna: {
      country: 'austria',
      name: 'Vienna',
      link: 'https://www.instagram.com/p/DdrVBDXiIJE/',
    },
    munich: {
      country: 'germany',
      name: 'Munich',
      link: 'https://www.instagram.com/p/DdbrQbKDazu/',
    },
    london: {
      country: 'unitedKingdom',
      name: 'London',
      link: 'https://www.instagram.com/p/DdrmhWyscPX/',
    },
    londonSep29: {
      country: 'unitedKingdom',
      name: 'London',
      link: 'https://www.instagram.com/kartonky.london/',
    },
    londonOct9: {
      country: 'unitedKingdom',
      name: 'London',
      link: 'https://www.instagram.com/kartonky.london/',
    },
    londonOct10: {
      country: 'unitedKingdom',
      name: 'London',
      link: 'https://www.instagram.com/kartonky.london/',
    },
    londonOct1: {
      country: 'unitedKingdom',
      name: 'London',
      link: 'https://www.instagram.com/p/Dd6KMBZMPC4/',
    },
    londonOct3: {
      country: 'unitedKingdom',
      name: 'London',
      link: 'https://www.instagram.com/p/Dd6KMBZMPC4/',
    },
  },
  days: [
    {
      dayKey: 'schedule26',
      monthKey: 'home.september',
      monthShortKey: 'home.sep',
      cities: ['sydney', 'edinburgh', 'manchester', 'paris'],
    },
    {
      dayKey: 'schedule27',
      monthKey: 'home.september',
      monthShortKey: 'home.sep',
      cities: ['vienna', 'london', 'lyon', 'gdansk', 'chicago'],
    },
    {
      dayKey: 'schedule28',
      monthKey: 'home.september',
      monthShortKey: 'home.sep',
      cities: ['brussels'],
    },
    {
      dayKey: 'schedule29',
      monthKey: 'home.september',
      monthShortKey: 'home.sep',
      cities: ['londonSep29'],
    },
    {
      dayKey: 'schedule30',
      monthKey: 'home.september',
      monthShortKey: 'home.sep',
      cities: ['parisSep30', 'budapest'],
    },
    {
      dayKey: 'schedule1',
      monthKey: 'home.october',
      monthShortKey: 'home.oct',
      cities: ['londonOct1'],
    },
    {
      dayKey: 'schedule2',
      monthKey: 'home.october',
      monthShortKey: 'home.oct',
      cities: ['ottawa'],
      stackWithPrevious: true,
    },
    {
      dayKey: 'schedule3',
      monthKey: 'home.october',
      monthShortKey: 'home.oct',
      cities: ['birmingham', 'munich', 'nantes', 'malaga', 'plovdiv', 'londonOct3', 'hamburg', 'oslo'],
    },
    {
      dayKey: 'schedule4',
      monthKey: 'home.october',
      monthShortKey: 'home.oct',
      cities: ['dublin', 'chicagoOct4', 'dresden', 'kiel'],
    },
    {
      dayKey: 'schedule5',
      monthKey: 'home.october',
      monthShortKey: 'home.oct',
      cities: ['leipzig'],
      stackWithPrevious: true,
    },
    {
      dayKey: 'schedule9',
      monthKey: 'home.october',
      monthShortKey: 'home.oct',
      cities: ['londonOct9'],
    },
    {
      dayKey: 'schedule10',
      monthKey: 'home.october',
      monthShortKey: 'home.oct',
      cities: ['londonOct10'],
    },
    {
      dayKey: 'schedule11',
      monthKey: 'home.october',
      monthShortKey: 'home.oct',
      cities: ['chicagoOct11', 'dublinOct11'],
    },
    {
      dayKey: 'schedule18',
      monthKey: 'home.october',
      monthShortKey: 'home.oct',
      cities: ['chicagoOct18'],
    },
    {
      dayKey: 'schedule25',
      monthKey: 'home.october',
      monthShortKey: 'home.oct',
      cities: ['dublinOct25'],
    },
    {
      dayKey: 'schedule31',
      monthKey: 'home.october',
      monthShortKey: 'home.oct',
      cities: ['nantesOct31'],
    },
  ],
  demands: {
    titleKey: 'rallies.oleshky2026.demandsTitle',
    itemKeys: [1, 2, 3, 4].map((n) => `rallies.oleshky2026.demand${n}`),
  },
  photos: oct2026Photos,
  posters: oct2026Posters,
  resources: oleshkyResources,
}

export const RALLIES = [december2025, oleshky2026]

export function rallyCityKeys(rally: Rally) {
  return rally.days.flatMap((day) => day.cities)
}
